import { AGREEMENT_TYPE } from 'services/templates/agreement-template/types';
import {
  IDistributionType,
  IContractTemplateTreeItemForm,
  SETTLEMENT_TYPE,
  TEMPLATE_TYPE,
} from 'services/templates/types';
import { flattenTree } from 'utils/dnd/dndUtilities';

export const supportsPerUnitSettlement = (type: IDistributionType): boolean =>
  type === TEMPLATE_TYPE.physicalDistribution ||
  type === TEMPLATE_TYPE.merchDistribution;

export const isPerUnitSettlement = (
  settlementType?: SETTLEMENT_TYPE
): boolean => settlementType === SETTLEMENT_TYPE.PerUnit;

export interface IDistributionSettlementSource {
  settlementType?: SETTLEMENT_TYPE | null;
  defaultSharePercentage?: number | null;
  defaultAmountPerUnit?: number | null;
  shares?: Array<{
    percent?: number | null;
    percentage?: number | null;
    amountPerUnit?: number | null;
  }>;
}

export const getSharePercentFromResponse = (share: {
  percent?: number | null;
  percentage?: number | null;
}): number | undefined => {
  const value = share.percent ?? share.percentage;
  return value ?? undefined;
};

export const inferSettlementTypeFromDistribution = (
  distribution: IDistributionSettlementSource
): SETTLEMENT_TYPE => {
  if (distribution.settlementType != null) {
    return distribution.settlementType;
  }

  const hasPerUnitDefault =
    distribution.defaultAmountPerUnit !== undefined &&
    distribution.defaultAmountPerUnit !== null;

  const hasPerUnitShare = distribution.shares?.some(
    share =>
      share.amountPerUnit !== undefined && share.amountPerUnit !== null
  );

  if (hasPerUnitDefault || hasPerUnitShare) {
    return SETTLEMENT_TYPE.PerUnit;
  }

  return SETTLEMENT_TYPE.Percentage;
};

export const getSettlementTypeByParentId = (
  items: IContractTemplateTreeItemForm[],
  parentId: string
): SETTLEMENT_TYPE => {
  const parent = flattenTree(items).find(item => item.id === parentId);
  return parent?.settlementType ?? SETTLEMENT_TYPE.Percentage;
};

const resetShareChild = (
  child: IContractTemplateTreeItemForm,
  settlementType: SETTLEMENT_TYPE
): IContractTemplateTreeItemForm => {
  if (
    child.type !== TEMPLATE_TYPE.agreement ||
    !child.content.distributionTemplateShares
  ) {
    return child;
  }

  return {
    ...child,
    settlementType,
    content: {
      ...child.content,
      distributionTemplateShares: {
        ...child.content.distributionTemplateShares,
        percent: undefined,
        amountPerUnit: undefined,
      },
    },
  };
};

export const toggleDistributionSettlementType = (
  items: IContractTemplateTreeItemForm[],
  distributionId: string
): IContractTemplateTreeItemForm[] => {
  const updateItems = (
    nodes: IContractTemplateTreeItemForm[]
  ): IContractTemplateTreeItemForm[] =>
    nodes.map(node => {
      if (node.id === distributionId) {
        const nextSettlementType = isPerUnitSettlement(node.settlementType)
          ? SETTLEMENT_TYPE.Percentage
          : SETTLEMENT_TYPE.PerUnit;

        return {
          ...node,
          settlementType: nextSettlementType,
          children: node.children.map(child =>
            resetShareChild(child, nextSettlementType)
          ),
        };
      }

      if (node.children.length) {
        return { ...node, children: updateItems(node.children) };
      }

      return node;
    });

  return updateItems(items);
};

export const mapShareFormToTemplateMutation = (
  settlementType: SETTLEMENT_TYPE,
  salesChannelId: string,
  share: { percent?: number; amountPerUnit?: number }
) => ({
  salesChannelId,
  ...(isPerUnitSettlement(settlementType)
    ? { amountPerUnit: share.amountPerUnit ?? 0 }
    : { percent: share.percent ?? 0 }),
});

export const mapShareFormToContractMutation = (
  settlementType: SETTLEMENT_TYPE,
  salesChannelId: string,
  share: { percent?: number; amountPerUnit?: number }
) => ({
  salesChannelId,
  ...(isPerUnitSettlement(settlementType)
    ? { amountPerUnit: share.amountPerUnit ?? 0 }
    : { percentage: share.percent ?? 0 }),
});

export const getDefaultShareFormValue = (
  settlementType: SETTLEMENT_TYPE,
  share?: { percent?: number | null; amountPerUnit?: number | null }
) =>
  isPerUnitSettlement(settlementType)
    ? { amountPerUnit: share?.amountPerUnit ?? undefined }
    : { percent: share?.percent ?? undefined };

export const isShareAgreement = (type?: AGREEMENT_TYPE) =>
  type === AGREEMENT_TYPE.shares || type === AGREEMENT_TYPE.defaultShare;

const isDistributionSection = (type: TEMPLATE_TYPE) =>
  type === TEMPLATE_TYPE.digitalDistribution ||
  type === TEMPLATE_TYPE.physicalDistribution ||
  type === TEMPLATE_TYPE.merchDistribution ||
  type === TEMPLATE_TYPE.channelDistribution;

const isShareValueMissing = (value: unknown) =>
  value === undefined || value === null || value === '';

export interface IDistributionShareValidationError {
  path: string;
  messageKey: string;
}

export const collectDistributionShareValidationErrors = (
  items: IContractTemplateTreeItemForm[],
  basePath = 'items'
): IDistributionShareValidationError[] => {
  const errors: IDistributionShareValidationError[] = [];

  const walk = (nodes: IContractTemplateTreeItemForm[], pathPrefix: string) => {
    for (let index = 0; index < nodes.length; index += 1) {
      const item = nodes[index];
      const itemPath = `${pathPrefix}[${index}]`;

      if (isDistributionSection(item.type)) {
        const settlementType = item.settlementType ?? SETTLEMENT_TYPE.Percentage;

        for (let childIndex = 0; childIndex < item.children.length; childIndex += 1) {
          const child = item.children[childIndex];

          if (child.type !== TEMPLATE_TYPE.agreement) {
            continue;
          }

          const shareType = child.content.type;
          if (!isShareAgreement(shareType)) {
            continue;
          }

          const share = child.content.distributionTemplateShares;
          const sharePath = `${itemPath}.children[${childIndex}].content.distributionTemplateShares`;

          if (
            shareType === AGREEMENT_TYPE.shares &&
            (share?.id === undefined || share.id === '')
          ) {
            errors.push({
              path: `${sharePath}.id`,
              messageKey: 'formik-error.required',
            });
          }

          if (isPerUnitSettlement(settlementType)) {
            if (isShareValueMissing(share?.amountPerUnit)) {
              errors.push({
                path: `${sharePath}.amountPerUnit`,
                messageKey: 'formik-error.required',
              });
            }
          } else if (isShareValueMissing(share?.percent)) {
            errors.push({
              path: `${sharePath}.percent`,
              messageKey: 'formik-error.required',
            });
          }
        }
      }

      if (item.children.length) {
        walk(item.children, `${itemPath}.children`);
      }
    }
  };

  walk(items, basePath);
  return errors;
};

export const findDistributionShareValidationError = (
  items: IContractTemplateTreeItemForm[]
): string | null => {
  const fieldErrors = collectDistributionShareValidationErrors(items);
  return fieldErrors[0]?.messageKey ?? null;
};
